import {
  SIGNUP_GET_AVAILABLE_DESIGNS, SIGNUP_UPDATE_MEMBERSHIP_TYPE,
  SIGNUP_GET_LATEST_DESIGN, SIGNUP_UPDATE_PROSPECT_ADDRESS,
  SIGNUP_CREATE_PROSPECT, SIGNUP_UPDATE_PROSPECT_ACCOUNT,
  SIGNUP_CREATE_VERIFICATION_PAYMENT, SIGNUP_GET_PROSPECT,
  SIGNUP_USE_COUPON_CODE, SIGNUP_ADD_WAITING_LIST
} from '@/utils/store/actionTypes'
import { serializeParameters } from '@/utils/general'
import { mapToWomenProspectProps, mapWomenAddressToProspectProps } from '@/utils/prospect'
import { idPrefixes } from '@/enums/signup'
import { getShieldSessionId } from '@/utils/shield'
import {
  FacebookClickIdCookie,
  FacebookBrowserIdCookie,
  OTASignupProspectIdCookie,
  UTMSourceCookie,
  UTMMediumCookie,
  UTMCampaignCookie,
  LandingPageUrlCookie,
  AttributionCookie,
  SpaazaReferralCodeCookie
} from '@/enums/gtm'
import { getCookieDomain } from '@/utils/cookie'

export default {
  async [SIGNUP_GET_AVAILABLE_DESIGNS] (_, { productGroupSizes = '' }) {
    const params = {
      free_signup: true,
      in_stock: true,
      product_group_sizes: productGroupSizes
    }

    const response = await this.$axios.$get(`${process.env.API_URL}/v2/designs?${serializeParameters(params)}`, {
      headers: {
        ContentType: 'application/json',
        Accept: 'application/json',
        'OTA-External': 'true' // Bypass axios plugin Authorization Header override
      }
    })

    return response
  },
  async [SIGNUP_GET_LATEST_DESIGN] (_, { women = false, men = true, addon = false, boys = true } = {}) {
    const params = serializeParameters({ women, addon, men, boys }).replace(/=true&/g, ',').replace(/=true/g, '')

    const response = await this.$axios.$get(`${process.env.API_URL}/v2/designs?latest=true&orientations=${params}`, {
      headers: {
        ContentType: 'application/json',
        Accept: 'application/json',
        'OTA-External': 'true' // Bypass axios plugin Authorization Header override
      }
    })

    return response
  },
  async [SIGNUP_CREATE_PROSPECT] ({ rootState }, variables) {
    const fbClickId = this.$cookies.get(FacebookClickIdCookie)
    const browserId = this.$cookies.get(FacebookBrowserIdCookie)
    const utmSource = this.$cookies.get(UTMSourceCookie)
    const utmMedium = this.$cookies.get(UTMMediumCookie)
    const utmCampaign = this.$cookies.get(UTMCampaignCookie)
    const landingPageUrl = this.$cookies.get(LandingPageUrlCookie)
    const otaAttribution = this.$cookies.get(AttributionCookie)
    const referralCode = this.$cookies.get(SpaazaReferralCodeCookie)

    const { uuid } = await this.$axios.$post(`${process.env.API_URL}/prospects`, {
      ...mapToWomenProspectProps({
        ...variables,
        country: variables.country || rootState.app.country,
        locale: rootState.app.locale,
        referralCode,
        fbClickId,
        browserId,
        utmSource,
        utmMedium,
        utmCampaign,
        landingPageUrl,
        otaAttribution
      })
    }, {
      headers: {
        ContentType: 'application/json',
        Accept: 'application/json',
        'OTA-External': 'true' // Bypass axios plugin Authorization Header override
      }
    })

    this.$cookies.set(OTASignupProspectIdCookie, uuid, { expires: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000), path: '/', domain: getCookieDomain() })

    return uuid
  },
  async [SIGNUP_UPDATE_PROSPECT_ACCOUNT] (_, variables) {
    const response = await this.$axios.$put(`${process.env.API_URL}/v2/prospects/${variables.prospectId}/account`, variables, {
      headers: {
        ContentType: 'application/json',
        Accept: 'application/json',
        'OTA-External': 'true' // Bypass axios plugin Authorization Header override
      }
    })

    return response
  },
  async [SIGNUP_UPDATE_PROSPECT_ADDRESS] ({ rootState }, { address, prospectId }) {
    const response = await this.$axios.$put(`${process.env.API_URL}/prospects/${prospectId}/address`, mapWomenAddressToProspectProps({ ...address, country: address.country || rootState.app.country }), {
      headers: {
        ContentType: 'application/json',
        Accept: 'application/json',
        'OTA-External': 'true' // Bypass axios plugin Authorization Header override
      }
    })

    return response
  },
  async [SIGNUP_UPDATE_MEMBERSHIP_TYPE] (_, { membershipType, productGroupSizeDesigns, prospectId }) {
    const response = await this.$axios.$put(`${process.env.API_URL}/prospects/${prospectId}/membership`, {
      membershipType: `${idPrefixes.membershipType}${membershipType}`,
      productGroupSizeDesigns
    }, {
      headers: {
        ContentType: 'application/json',
        Accept: 'application/json',
        'OTA-External': 'true' // Bypass axios plugin Authorization Header override
      }
    })

    return response
  },
  async [SIGNUP_CREATE_VERIFICATION_PAYMENT] ({ rootState }, { prospectId, paymentType = '', issuerId = null, paymentMethodNonce = null, successUrl = '', pendingUrl = '', failedUrl = '', declinedUrl = '', deviceData = '', externalIdentifier = null, billingMetadata = null }) {
    const shieldSessionId = await getShieldSessionId()

    const response = await this.$axios.$post(`${process.env.API_URL}/prospects/${prospectId}/verification-payment`, {
      language: rootState.app.locale,
      paymentType,
      issuerId,
      paymentMethodNonce,
      deviceData,
      successUrl,
      pendingUrl,
      failedUrl,
      declinedUrl,
      shieldSessionId: shieldSessionId ?? null,
      externalIdentifier,
      billingMetadata
    },
    {
      headers: {
        ContentType: 'application/json',
        Accept: 'application/json',
        'OTA-External': 'true' // Bypass axios plugin Authorization Header override
      }
    })

    return response
  },
  async [SIGNUP_GET_PROSPECT] (_, { uuid, _hash }) {
    const response = await this.$axios.$get(`${process.env.API_URL}/v2/prospect/${uuid}?_hash=${encodeURIComponent(_hash)}`,
      {
        headers: {
          ContentType: 'application/json',
          Accept: 'application/json'
        }
      })
    return response
  },
  async [SIGNUP_USE_COUPON_CODE] (_, { prospectId, couponCode }) {
    const response = await this.$axios.$put(`${process.env.API_URL}/v2/prospects/${prospectId}/coupon-code`,
      {
        couponCode: `${couponCode}`.trim()
      },
      {
        headers: {
          ContentType: 'application/json',
          Accept: 'application/json',
          'OTA-External': 'true' // Bypass axios plugin Authorization Header override
        }
      })

    return response
  },
  async [SIGNUP_ADD_WAITING_LIST] (_, { firstName, lastName, email, design, productGroupSize }) {
    const response = await this.$axios.$post(`${process.env.API_URL}/v2/waiting-list`,
      {
        firstName,
        lastName,
        email,
        design,
        productGroupSize
      },
      {
        headers: {
          ContentType: 'application/json',
          Accept: 'application/json',
          'OTA-External': 'true' // Bypass axios plugin Authorization Header override
        }
      })

    return response
  }
}
